import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanySelector } from "@/hooks/useCompanySelector";
import { useAllAnnotationCounts } from "@/hooks/useDocumentAnnotations";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "sonner";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  Upload,
  ExternalLink,
  Download,
  Trash2,
  Copy,
  MessageSquare,
  File,
  Ruler,
  Eye,
  MoreVertical,
} from "lucide-react";


interface ProjectDocumentsTabProps {
  projectId: string;
  onOpenInTakeoff?: (documentId: string) => void;
}

interface ProjectDocument {
  id: string;
  project_id: string;
  company_id: string;
  uploaded_by: string;
  file_name: string;
  file_path: string;
  file_size: number;
  mime_type: string | null;
  notes: string | null;
  source: string;
  source_message_id: string | null;
  created_at: string;
  uploader_name?: string;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function getFileIcon(mimeType: string | null): string {
  if (!mimeType) return "📄";
  if (mimeType.includes("pdf")) return "📕";
  if (mimeType.includes("spreadsheet") || mimeType.includes("excel") || mimeType.includes("csv")) return "📊";
  if (mimeType.includes("image")) return "🖼️";
  if (mimeType.includes("word") || mimeType.includes("document")) return "📝";
  return "📄";
}

function isTakeoffCompatible(mimeType: string | null): boolean {
  if (!mimeType) return false;
  return mimeType.includes("pdf") || mimeType.startsWith("image");
}

/* ─── Mobile document card ─── */
const MobileDocCard = ({
  doc,
  annotationCount,
  onView,
  onTakeoff,
  onDownload,
  onDelete,
}: {
  doc: ProjectDocument;
  annotationCount: number;
  onView: () => void;
  onTakeoff: () => void;
  onDownload: () => void;
  onDelete: () => void;
}) => {
  const compatible = isTakeoffCompatible(doc.mime_type);
  return (
    <Card className="border bg-card">
      <CardContent className="p-3 space-y-2">
        <div className="flex items-start gap-2">
          <span className="text-xl shrink-0">{getFileIcon(doc.mime_type)}</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{doc.file_name}</p>
            <p className="text-xs text-muted-foreground">
              {doc.uploader_name} · {format(new Date(doc.created_at), "MMM d, yyyy")}
            </p>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <span className="text-[10px] text-muted-foreground">{formatFileSize(doc.file_size)}</span>
              <Badge
                variant={doc.source === "comms" ? "secondary" : "outline"}
                className="text-[10px] px-1.5 py-0"
              >
                {doc.source === "comms" ? "Comms" : "Upload"}
              </Badge>
              {annotationCount > 0 && (
                <Badge variant="secondary" className="text-[10px] gap-1 px-1.5 py-0">
                  <Ruler className="h-2.5 w-2.5" /> Takeoff
                </Badge>
              )}
            </div>
          </div>
        </div>
        {doc.notes && (
          <p className="text-xs text-muted-foreground line-clamp-2">{doc.notes}</p>
        )}
        <div className="flex gap-1.5 pt-1">
          <Button variant="outline" size="sm" className="flex-1 text-xs h-8" onClick={onView}>
            <Eye className="h-3 w-3 mr-1" /> View
          </Button>
          {compatible && (
            <Button variant="outline" size="sm" className="flex-1 text-xs h-8" onClick={onTakeoff}>
              <Ruler className="h-3 w-3 mr-1" /> Takeoff
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onDownload}>
            <Download className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-destructive" onClick={onDelete}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

/* ─── View vs Takeoff choice popover ─── */
const DocActionPopover = ({
  children,
  onView,
  onTakeoff,
}: {
  children: React.ReactNode;
  onView: () => void;
  onTakeoff: () => void;
}) => (
  <Popover>
    <PopoverTrigger asChild>{children}</PopoverTrigger>
    <PopoverContent className="w-44 p-1.5" align="start">
      <button
        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent transition-colors"
        onClick={onView}
      >
        <Eye className="h-4 w-4" /> View Document
      </button>
      <button
        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent transition-colors"
        onClick={onTakeoff}
      >
        <Ruler className="h-4 w-4" /> Open in Takeoff
      </button>
    </PopoverContent>
  </Popover>
);

export const ProjectDocumentsTab = ({ projectId, onOpenInTakeoff }: ProjectDocumentsTabProps) => {
  const { user } = useAuth();
  const { activeCompanyId } = useCompanySelector();
  const isMobile = useIsMobile();
  const qc = useQueryClient();
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);

  // Fetch documents
  const { data: documents = [], isLoading } = useQuery({
    queryKey: ["project_documents", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_documents")
        .select("*")
        .eq("project_id", projectId)
        .order("created_at", { ascending: false });
      if (error) throw error;

      const userIds = [...new Set((data ?? []).map((d: any) => d.uploaded_by))];
      let profileMap: Record<string, string> = {};
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from("user_profiles")
          .select("user_id, first_name, last_name")
          .in("user_id", userIds);
        (profiles ?? []).forEach((p: any) => {
          profileMap[p.user_id] = [p.first_name, p.last_name].filter(Boolean).join(" ") || "Unknown";
        });
      }

      return (data ?? []).map((d: any) => ({
        ...d,
        uploader_name: profileMap[d.uploaded_by] || "Unknown",
      })) as ProjectDocument[];
    },
    enabled: !!projectId,
  });

  // Annotation counts for takeoff badges
  const docIds = documents.map((d) => d.id);
  const { data: annotationCounts = {} } = useAllAnnotationCounts(docIds);

  // Upload handler
  const handleUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeCompanyId || !user) return;

    setUploading(true);
    try {
      const filePath = `${activeCompanyId}/${projectId}/${Date.now()}_${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from("project-documents")
        .upload(filePath, file);
      if (uploadError) throw uploadError;

      const { error: insertError } = await supabase
        .from("project_documents")
        .insert({
          project_id: projectId,
          company_id: activeCompanyId,
          uploaded_by: user.id,
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          mime_type: file.type || null,
          notes: notes.trim() || null,
          source: "upload",
        });
      if (insertError) throw insertError;

      toast.success(`Uploaded "${file.name}"`);
      setNotes("");
      qc.invalidateQueries({ queryKey: ["project_documents", projectId] });
    } catch (err: any) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }, [activeCompanyId, projectId, user, notes, qc]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (doc: ProjectDocument) => {
      await supabase.storage.from("project-documents").remove([doc.file_path]);
      const { error } = await supabase
        .from("project_documents")
        .delete()
        .eq("id", doc.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Document deleted");
      qc.invalidateQueries({ queryKey: ["project_documents", projectId] });
    },
    onError: (err: any) => toast.error(err.message),
  });

  const openDocument = async (doc: ProjectDocument) => {
    const { data, error } = await supabase.storage
      .from("project-documents")
      .createSignedUrl(doc.file_path, 3600);
    if (error || !data?.signedUrl) {
      toast.error("Could not generate download link");
      return;
    }
    window.open(data.signedUrl, "_blank");
  };

  const downloadDocument = async (doc: ProjectDocument) => {
    const { data, error } = await supabase.storage
      .from("project-documents")
      .createSignedUrl(doc.file_path, 3600);
    if (error || !data?.signedUrl) {
      toast.error("Could not generate download link");
      return;
    }
    const a = document.createElement("a");
    a.href = data.signedUrl;
    a.download = doc.file_name;
    a.click();
  };

  const copyLink = async (doc: ProjectDocument) => {
    const { data, error } = await supabase.storage
      .from("project-documents")
      .createSignedUrl(doc.file_path, 86400);
    if (error || !data?.signedUrl) {
      toast.error("Could not generate link");
      return;
    }
    navigator.clipboard.writeText(data.signedUrl);
    toast.success("Link copied (expires in 24h)");
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end p-4 rounded-xl border bg-card">
        <div className="flex-1 space-y-2 w-full">
          <label className="text-sm font-medium text-foreground">Upload Document</label>
          <Textarea
            placeholder="Optional notes about this document…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="resize-none"
          />
        </div>
        <div className="flex-shrink-0">
          <label htmlFor="doc-upload">
            <Button asChild disabled={uploading} className="cursor-pointer">
              <span>
                <Upload className="h-4 w-4 mr-1.5" />
                {uploading ? "Uploading…" : "Choose File"}
              </span>
            </Button>
          </label>
          <input
            id="doc-upload"
            type="file"
            className="hidden"
            onChange={handleUpload}
            disabled={uploading}
          />
        </div>
      </div>

      {/* Mobile: Card layout */}
      {isMobile ? (
        <div className="space-y-2">
          {isLoading ? (
            <p className="text-center text-muted-foreground py-8">Loading documents…</p>
          ) : documents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <File className="h-8 w-8 mx-auto mb-2 opacity-30" />
              No documents uploaded yet
            </div>
          ) : (
            documents.map((doc) => (
              <MobileDocCard
                key={doc.id}
                doc={doc}
                annotationCount={annotationCounts[doc.id] || 0}
                onView={() => openDocument(doc)}
                onTakeoff={() => onOpenInTakeoff?.(doc.id)}
                onDownload={() => downloadDocument(doc)}
                onDelete={() => deleteMutation.mutate(doc)}
              />
            ))
          )}
        </div>
      ) : (
        /* Desktop: Table layout */
        <div className="rounded-xl border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>File Name</TableHead>
                <TableHead className="hidden md:table-cell">Size</TableHead>
                <TableHead>Uploaded By</TableHead>
                <TableHead>Date / Time</TableHead>
                <TableHead className="hidden lg:table-cell">Notes</TableHead>
                <TableHead className="w-20">Source</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    Loading documents…
                  </TableCell>
                </TableRow>
              ) : documents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    <File className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    No documents uploaded yet
                  </TableCell>
                </TableRow>
              ) : (
                documents.map((doc) => (
                  <ContextMenu key={doc.id}>
                    <ContextMenuTrigger asChild>
                      <TableRow className="cursor-context-menu">
                        <TableCell className="text-center text-lg">
                          {getFileIcon(doc.mime_type)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {isTakeoffCompatible(doc.mime_type) ? (
                              <DocActionPopover
                                onView={() => openDocument(doc)}
                                onTakeoff={() => onOpenInTakeoff?.(doc.id)}
                              >
                                <button className="text-primary hover:underline font-medium text-left">
                                  {doc.file_name}
                                </button>
                              </DocActionPopover>
                            ) : (
                              <button
                                className="text-primary hover:underline font-medium text-left"
                                onClick={() => openDocument(doc)}
                              >
                                {doc.file_name}
                              </button>
                            )}
                            {annotationCounts[doc.id] > 0 && (
                              <Badge variant="secondary" className="text-[10px] gap-1 px-1.5 py-0">
                                <Ruler className="h-2.5 w-2.5" />
                                Takeoff
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-muted-foreground text-xs">
                          {formatFileSize(doc.file_size)}
                        </TableCell>
                        <TableCell className="text-sm">{doc.uploader_name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {format(new Date(doc.created_at), "MMM d, yyyy h:mm a")}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm text-muted-foreground max-w-[200px] truncate">
                          {doc.notes || "—"}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={doc.source === "comms" ? "secondary" : "outline"}
                            className="text-xs"
                          >
                            {doc.source === "comms" ? (
                              <><MessageSquare className="h-3 w-3 mr-1" />Comms</>
                            ) : (
                              <><Upload className="h-3 w-3 mr-1" />Upload</>
                            )}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    </ContextMenuTrigger>
                    <ContextMenuContent className="w-52">
                      {isTakeoffCompatible(doc.mime_type) && (
                        <>
                          <ContextMenuItem onClick={() => onOpenInTakeoff?.(doc.id)} className="gap-2">
                            <Ruler className="h-4 w-4" /> Open in Takeoff Viewer
                          </ContextMenuItem>
                          <ContextMenuSeparator />
                        </>
                      )}
                      <ContextMenuItem onClick={() => openDocument(doc)} className="gap-2">
                        <ExternalLink className="h-4 w-4" /> Open in New Tab
                      </ContextMenuItem>
                      <ContextMenuItem onClick={() => downloadDocument(doc)} className="gap-2">
                        <Download className="h-4 w-4" /> Download
                      </ContextMenuItem>
                      <ContextMenuItem onClick={() => copyLink(doc)} className="gap-2">
                        <Copy className="h-4 w-4" /> Copy Link
                      </ContextMenuItem>
                      <ContextMenuSeparator />
                      <ContextMenuItem
                        onClick={() => deleteMutation.mutate(doc)}
                        className="gap-2 text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" /> Delete
                      </ContextMenuItem>
                    </ContextMenuContent>
                  </ContextMenu>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

    </div>
  );
};
